import * as React from 'react'
import { ACTION_LABEL, visibleModResources, type ModifyResourceSpec } from './constants'
import { cn } from '../../../lib/utils'
import { useHiddenIngestSources } from './useHiddenIngestSources'
import { WizardChrome } from './WizardChrome'
import type { ModifyAction, ModifyResourceId } from './types'
import type { IngestWizardApi } from './useIngestWizard'

interface StepModifyPickProps {
  wizard: IngestWizardApi
}

export function StepModifyPick({ wizard }: StepModifyPickProps) {
  const { state, siteName, pickModResource, pickModAction, goModifyDetail, backTo1 } = wizard
  const ready = !!state.modResource && !!state.modAction
  const resources = visibleModResources(useHiddenIngestSources())

  return (
    <WizardChrome
      eyebrow="Step 02 — Change"
      title={`What are you changing on ${siteName || 'this site'}?`}
      description="One resource and one action per session. Only supported actions are shown."
      onBack={backTo1}
      onContinue={goModifyDetail}
      continueDisabled={!ready}
    >
      <div className="flex flex-col gap-1.5">
        {resources.map((res) => {
          const selected = state.modResource === res.id
          const availableLabels = res.actions
            .filter((a) => a.available)
            .map((a) => ACTION_LABEL[a.id])
            .join(' · ')
          return (
            <div
              key={res.id}
              className={cn(
                'overflow-hidden rounded-lg border transition-[border-color,background-color] duration-200 ease-out motion-reduce:transition-none',
                selected ? 'border-brand/40 bg-brand-soft/20' : 'border-line bg-bg-elev'
              )}
            >
              <button
                type="button"
                onClick={() => pickModResource(res.id as ModifyResourceId)}
                className="flex w-full gap-2.5 border-0 bg-transparent px-3.5 py-3 text-left transition-colors duration-200 hover:bg-bg-sunk/40 motion-reduce:transition-none"
              >
                <span
                  className={cn(
                    'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border',
                    selected ? 'border-brand' : 'border-line-2'
                  )}
                >
                  {selected ? <span className="size-2 rounded-full bg-brand" /> : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-14 font-medium text-ink">{res.label}</span>
                  <span className="mt-1 block text-xs leading-snug text-ink-3">{res.desc}</span>
                </span>
                <span className="shrink-0 font-mono text-11 text-ink-3">{availableLabels}</span>
              </button>
              <ModifyActionPicker
                resource={res}
                open={selected}
                selectedAction={state.modAction}
                onActionChange={pickModAction}
              />
            </div>
          )
        })}
      </div>
    </WizardChrome>
  )
}

function ModifyActionPicker({
  resource,
  open,
  selectedAction,
  onActionChange
}: {
  resource: ModifyResourceSpec
  open: boolean
  selectedAction: ModifyAction | null
  onActionChange: (action: ModifyAction) => void
}) {
  // Keep the controls mounted until the close animation completes.
  const [mounted, setMounted] = React.useState(open)

  React.useEffect(() => {
    if (open) {
      setMounted(true)
      return undefined
    }
    const timer = setTimeout(() => setMounted(false), 200)
    return () => clearTimeout(timer)
  }, [open])

  return (
    <div
      data-open={open}
      className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none data-[open=true]:grid-rows-[1fr]"
    >
      <div className="min-h-0 overflow-hidden">
        {mounted ? (
          <div
            data-open={open}
            className="flex translate-y-[-2px] flex-col gap-2 px-3.5 pb-3 pl-10.25 opacity-0 transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none data-[open=true]:translate-y-0 data-[open=true]:opacity-100"
          >
            <span className="text-11 font-medium tracking-[0.08em] text-ink-3 uppercase">
              Action
            </span>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Modify action">
              {resource.actions
                .filter((act) => act.available)
                .map((act) => {
                  const active = selectedAction === act.id
                  return (
                    <button
                      key={act.id}
                      type="button"
                      aria-pressed={active}
                      onClick={() => onActionChange(act.id)}
                      className={cn(
                        'h-7 rounded-md border px-2.5 text-xs font-medium transition-colors duration-200 motion-reduce:transition-none',
                        active
                          ? 'border-brand bg-brand-soft text-brand'
                          : 'border-line bg-bg-elev text-ink hover:bg-bg-sunk'
                      )}
                    >
                      {ACTION_LABEL[act.id]}
                    </button>
                  )
                })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
